import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { hasModule } from "@/lib/access";
import { Building } from "lucide-react";

const Cell = ({ value, onClick, tone, testid }) => (
  <td className="px-3 py-2.5 text-right">
    {onClick ? (
      <button type="button" data-testid={testid} onClick={onClick}
        className={`mono text-sm px-2 py-0.5 rounded-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${value && tone ? tone : "text-slate-700"}`}>{value}</button>
    ) : <span className="mono text-sm text-slate-700" data-testid={testid}>{value}</span>}
  </td>
);

export const DepartmentReport = ({ user }) => {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/reports/departments").then((r) => setData(r.data)).catch(() => setData({ rows: [] })); }, []);
  if (!data || data.rows.length === 0) return null;
  const ob = hasModule(user, "obligations");
  const tk = hasModule(user, "tasks");
  const cc = hasModule(user, "compliance");
  const month = new Date(`${data.month}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" });
  return (
    <section className="mt-10 bg-white border border-blue-100 rounded-lg overflow-hidden" data-testid="dept-report">
      <div className="px-5 py-3 border-b border-blue-100 bg-[#F5F9FF] flex items-center gap-2">
        <Building className="w-4 h-4 text-blue-700" />
        <h2 className="text-sm font-semibold text-slate-900">Departments</h2>
        <span className="text-xs text-slate-500">· hours for {month}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="text-left px-5 py-2 font-medium">Department</th>
              <th className="text-right px-3 py-2 font-medium">People</th>
              {ob && <><th className="text-right px-3 py-2 font-medium">Open obligations</th><th className="text-right px-3 py-2 font-medium">Overdue obligations</th></>}
              {cc && <><th className="text-right px-3 py-2 font-medium">Open filings</th><th className="text-right px-3 py-2 font-medium">Overdue filings</th></>}
              {tk && <><th className="text-right px-3 py-2 font-medium">Open tasks</th><th className="text-right px-3 py-2 font-medium">Overdue tasks</th><th className="text-right px-5 py-2 font-medium">Hours</th></>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.id} data-testid={`dept-report-row-${r.id}`} className="hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium text-slate-900">{r.name}</td>
                <Cell value={r.people} testid={`dept-report-people-${r.id}`} />
                {ob && <>
                  <Cell value={r.obligations_open} testid={`dept-report-ob-open-${r.id}`} onClick={() => nav(`/obligations?scope=team&department=${r.id}`)} />
                  <Cell value={r.obligations_overdue} tone="text-rose-700" testid={`dept-report-ob-overdue-${r.id}`} onClick={() => nav(`/obligations?scope=team&department=${r.id}&status=overdue`)} />
                </>}
                {cc && <>
                  <Cell value={r.compliance_open} testid={`dept-report-cc-open-${r.id}`} onClick={() => nav(`/compliance/register?scope=team&department=${r.id}`)} />
                  <Cell value={r.compliance_overdue} tone="text-rose-700" testid={`dept-report-cc-overdue-${r.id}`} onClick={() => nav(`/compliance/register?scope=team&department=${r.id}&status=overdue`)} />
                </>}
                {tk && <>
                  <Cell value={r.tasks_open} testid={`dept-report-tk-open-${r.id}`} onClick={() => nav(`/tasks?department=${r.id}&flat=1`)} />
                  <Cell value={r.tasks_overdue} tone="text-rose-700" testid={`dept-report-tk-overdue-${r.id}`} onClick={() => nav(`/tasks?department=${r.id}&due=overdue`)} />
                  <td className="px-5 py-2.5 text-right mono text-sm text-slate-700" data-testid={`dept-report-hours-${r.id}`}>{r.hours_month.toFixed(1)}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
